import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { UtensilsCrossed, Leaf } from "lucide-react";
import { CUISINE_MENU, CUISINE_GALLERY, IMG } from "@/data/site";
import FoodCarousel from "@/components/FoodCarousel";
import Lightbox from "@/components/Lightbox";

export default function Cuisine() {
  const [lightbox, setLightbox] = useState(null);
  const menuRef = useRef(null);

  useEffect(() => {
    const el = menuRef.current;
    if (!el) return;
    const items = el.querySelectorAll(".menu-item");
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          items.forEach((li, i) => {
            setTimeout(() => li.classList.add("is-lit"), i * 140);
          });
          io.disconnect();
        }
      },
      { threshold: 0.2 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <section
      id="cuisine"
      className="relative bg-[var(--pam-bg)] overflow-hidden"
      style={{ padding: "5% 0" }}
      data-testid="cuisine-section"
    >
      <div className="container-pam">
        {/* Header */}
        <div className="max-w-3xl">
          <p className="overline gold-text">Authentic Malnad cuisine</p>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.7 }}
            className="mt-4 font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-[var(--pam-forest)]"
          >
            Home-cooked over a slow flame,
            <span className="italic text-[var(--pam-coffee)]">
              {" "}served on a wooden Malnad table.
            </span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="mt-6 text-[var(--pam-muted)] leading-relaxed max-w-2xl"
          >
            Every kitchen in our partner homestay network cooks by heart, not by
            recipe card. From akki rotti and kadubu to koli saaru, neer dosa and
            traditional banana-leaf meals — every plate is set on a hand-crafted
            wagon-wheel wooden dining table, in true Malnad style.
          </motion.p>
        </div>

        {/* Feature dining table image */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.8 }}
          className="mt-14 relative rounded-[28px] overflow-hidden shadow-2xl"
        >
          <img
            src={IMG.diningTable}
            alt="Traditional wooden wagon-wheel dining table at Peak Adventures Malnad"
            loading="lazy"
            className="w-full h-[360px] md:h-[520px] object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-tr from-black/60 via-black/10 to-transparent" />
          <div className="absolute bottom-6 left-6 md:bottom-10 md:left-10 text-white max-w-md">
            <div className="inline-flex items-center gap-2 text-[var(--pam-gold)]">
              <UtensilsCrossed className="w-4 h-4" />
              <span className="overline">The Malnad table</span>
            </div>
            <h3 className="mt-2 font-serif text-2xl md:text-4xl">
              Where every meal turns into a memory.
            </h3>
          </div>
        </motion.div>

        {/* Carousel + Menu */}
        <div className="mt-16 grid md:grid-cols-12 gap-8 md:gap-10 items-start">
          <div className="md:col-span-7">
            <FoodCarousel items={CUISINE_GALLERY} onOpen={setLightbox} />
            <p className="mt-4 text-xs text-[var(--pam-muted)]">
              Tap any dish to view it full-screen · Swipe to browse
            </p>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.7 }}
            className="md:col-span-5 bg-white border border-[var(--pam-forest)]/10 rounded-2xl p-7 md:p-9 shadow-sm"
            data-testid="cuisine-menu"
            ref={menuRef}
          >
            <div className="inline-flex items-center gap-2 text-[var(--pam-gold)]">
              <Leaf className="w-4 h-4" />
              <span className="overline">On the menu</span>
            </div>
            <h3 className="mt-3 font-serif text-2xl md:text-3xl text-[var(--pam-forest)]">
              Everyday specials
            </h3>

            {/* Desktop: static grid — animated light-sweep reveal */}
            <ul className="mt-6 hidden md:grid grid-cols-1 gap-y-3">
              {CUISINE_MENU.map((m) => (
                <li
                  key={m}
                  className="menu-item flex items-start gap-3"
                  data-testid={`menu-item-${m.split(" ")[0].toLowerCase()}`}
                >
                  <span
                    className="mt-2 w-1.5 h-1.5 rounded-full bg-[var(--pam-gold)] shrink-0"
                    aria-hidden="true"
                  />
                  <span className="text-[15px] leading-relaxed">{m}</span>
                </li>
              ))}
            </ul>

            {/* Mobile: vertical auto-scrolling ticker */}
            <div
              className="pam-ticker-wrap mt-6 md:hidden"
              style={{ height: "220px" }}
              data-testid="menu-ticker-mobile"
            >
              <ul className="pam-ticker-track">
                {[...CUISINE_MENU, ...CUISINE_MENU].map((m, i) => (
                  <li
                    key={`${m}-${i}`}
                    className="flex items-start gap-3 py-2"
                    data-testid={i < CUISINE_MENU.length ? `menu-ticker-item-${m.split(" ")[0].toLowerCase()}` : undefined}
                  >
                    <span
                      className="mt-2 w-1.5 h-1.5 rounded-full bg-[var(--pam-gold)] shrink-0"
                      aria-hidden="true"
                    />
                    <span className="text-[15px] leading-relaxed text-[var(--pam-text)]">
                      {m}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>
        </div>
      </div>

      <Lightbox image={lightbox} onClose={() => setLightbox(null)} />
    </section>
  );
}
